import {createSelector} from 'reselect'
import {filter, has, pathOr, pipe, propEq, values} from 'ramda'
import {getActivePortfolioId} from '@/store/app/selectors'
import {isActive} from '@/rules-helper'
import {prependIndexerString} from './util'

const getPortfoliosState = (state) => state.portfolios

export const getPortfolios = createSelector(
  [getPortfoliosState],
  (portfolios) => values(portfolios) || [],
)

export const getActivePortfolio = createSelector(
  [getActivePortfolioId, getPortfoliosState],
  (activeId, portfolios) => portfolios[prependIndexerString(activeId)] || {}
)

export const getActivePortfolioName = createSelector(
  [getActivePortfolio],
  (active) => active.name || '',
)

export const isActivePortfolioDefault = createSelector(
  [getActivePortfolio],
  (active) => has('isDefault', active) ? active.isDefault && !active.sharedByUserName : true
)

export const isActivePortfolioShared = createSelector(
  [getActivePortfolio],
  (active) => !!active.sharedByUserName,
)

export const getActivePortfolioEventsSummary = createSelector(
  [getActivePortfolio],
  (active) => active.notificationEventsSummary || {},
)

export const getActivePortfolioNotificationEvents = createSelector(
  [getActivePortfolio],
  (active) => active.notificationEvents || [],
)
export const getFilteredPortfolioNotificationEvents = createSelector(
  [getActivePortfolio],
  (active) => active.filteredNotificationEvents || null,
)

export const getActivePortfolioNotificationEventsCount = createSelector(
  [getActivePortfolio],
  (active) => active.notificationEventsCount || 0,
)

export const getActivePortfolioModalEvents = createSelector(
  [getActivePortfolio],
  (active) => active.modalEvents || [],
)

export const getActivePortfolioModalEventsCount = createSelector(
  [getActivePortfolio],
  (active) => active.modalEventsCount || 0,
)

export const getActivePortfolioModalEventsLoader = createSelector(
  [getActivePortfolio],
  (active) => active.modalEventsLoader || false,
)

export const getActivePortfolioCompanies = createSelector(
  [getActivePortfolio],
  (active) => values(active.companies),
)

export const getActivePortfolioCompaniesCount = createSelector(
  [getActivePortfolio],
  (active) => active.companiesCount || 0,
)

export const getActivePortfolioCompaniesMap = createSelector(
  [getActivePortfolio],
  (active) => active.companies,
)

export const getActivePortfolioCompaniesByRiskType = createSelector(
  [getActivePortfolio],
  (active) => values(active.companiesByRiskType),
)

export const getActivePortfolioCompaniesByRiskTypeCount = createSelector(
  [getActivePortfolio],
  (active) => active.companiesByRiskTypeCount || 0,
)

const getCountryFilter = (state) => state.ui.portfolioCountryFilter

export const getActivePortfolioFilteredCompanies = createSelector(
  [getActivePortfolioCompanies, getCountryFilter],
  (companies, countryFilter) => {
    return countryFilter === 'all'
      ? companies
      : filter(propEq('countryCode', countryFilter), companies)
  }
)

export const getActivePortfolioCountries = createSelector(
  [getActivePortfolio],
  (active) => active.countries || [],
)

const getExports = (type) => pipe(
  pathOr({}, ['exports', type]),
  values,
)

export const getActivePortfolioPreviousExports = createSelector(
  [getActivePortfolio],
  getExports('previous'),
)

export const getActivePortfolioPreviousExportsCount = createSelector(
  [getActivePortfolio],
  (active) => active.exports ? active.exports.previousCount : 0
)

export const getActivePortfolioScheduledExports = createSelector(
  [getActivePortfolio],
  getExports('scheduled'),
)

export const getActivePortfolioScheduledExportsCount = createSelector(
  [getActivePortfolio],
  (active) => active.exports ? active.exports.scheduledCount : 0
)

export const getActivePortfolioScheduledExportsMap = createSelector(
  [getActivePortfolio],
  pathOr({}, ['exports', 'scheduled'])
)

export const getActivePortfolioRules = createSelector(
  [getActivePortfolio],
  (active) => active.rules || {},
)

export const getActivePortfolioGlobalRules = createSelector(
  [getActivePortfolioRules],
  (rules) => rules['XX'] || {},
)

export const getActivePortfolioRuleCountriesChanged = createSelector(
  [getActivePortfolio],
  (active) => active.ruleCountriesChanged || [],
)

export const getActivePortfolioGraphData = createSelector(
  [getActivePortfolio],
  (active) => active.graphData || [],
)

export const ruleActive = (ruleCode) => createSelector(
  [getActivePortfolioRules],
  isActive(ruleCode)
)

export const getActivePortfolioRiskSummary = createSelector(
  [getActivePortfolio],
  (active) => active.riskSummary || {},
)

export const getSelectedCompanies = createSelector(
  [getActivePortfolio],
  (active) => active.selectedCompanies || {isAll: false, data: []},
)

export const getActivePortfolioPermissions = createSelector(
  [getActivePortfolio],
  (active) => active.permissions || ''
)



// WEBPACK FOOTER //
// ./src/store/portfolios/selectors.js